import React, { useEffect } from 'react';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

interface NotificationProps {
  message: string;
  type: NotificationType;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

const Notification: React.FC<NotificationProps> = ({
  message,
  type,
  isVisible,
  onClose,
  duration = 5000,
}) => {
  // Auto-dismiss after the specified duration
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);
  
  if (!isVisible) return null;
  
  const typeStyles = {
    info: 'bg-blue-500/90 text-white',
    success: 'bg-green-500/90 text-white',
    warning: 'bg-amber-500/90 text-white',
    error: 'bg-red-500/90 text-white',
  };
  
  // Handle closing notification while restoring focus to editor
  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    onClose();
    
    // Return focus to the editor textarea after a brief delay
    setTimeout(() => {
      const textArea = document.querySelector('.editor-container textarea') as HTMLTextAreaElement;
      if (textArea) {
        textArea.focus();
      }
    }, 10);
  };
  
  return (
    <div 
      className={`fixed z-50 top-4 left-1/2 transform -translate-x-1/2 px-4 py-3 rounded-lg shadow-lg 
        ${typeStyles[type]} max-w-md w-11/12 flex items-center justify-between notification animate-fade-in`}
      role="alert"
    >
      <span className="flex-1">{message}</span>
      <button 
        onClick={handleClose}
        className="ml-3 text-white hover:text-gray-100 rounded-full h-6 w-6 flex items-center justify-center focus:outline-none"
        aria-label="Close notification"
      >
        ×
      </button>
    </div>
  );
};

export default Notification; 